import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { MessageCircle, Send, Phone, ClipboardList, RefreshCw, Search, User, CreditCard, CheckCircle2, Clock } from "lucide-react";

export const UraSolicitacoes = ({ onContatoWhatsapp }) => {
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState("");

  const carregar = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/voip/receptivo/solicitacoes?limit=500`);
      const dados = Array.isArray(r.data) ? r.data : (r.data?.solicitacoes || []);
      setLista(dados);
    } catch (e) {
      toast.error("Erro ao carregar solicitações da URA");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const fmtData = (d) => { try { return new Date(d).toLocaleString("pt-BR"); } catch { return d || "-"; } };
  const telLimpo = (t) => (t || "").replace(/\D/g, "");

  const registrarOcorrencia = async (sol, textoFixo, tipo, silent) => {
    if (!sol.id) { if (!silent) toast.error("Registro sem ID"); return; }
    const texto = textoFixo || window.prompt("Ocorrência do atendimento:", "");
    if (!texto) return;
    try {
      await axios.post(`${API}/voip/receptivo/solicitacoes/${sol.id}/ocorrencia`, { texto, tipo: tipo || "NOTA" });
      if (!silent) toast.success("Ocorrência registrada!");
      carregar();
    } catch (e) { if (!silent) toast.error("Erro ao registrar ocorrência"); }
  };

  const setStatus = async (sol, status, textoFixo) => {
    if (!sol.id) { toast.error("Registro sem ID"); return; }
    const texto = textoFixo !== undefined ? textoFixo : (window.prompt("Ocorrência do atendimento (finalização):", "") || "");
    if (status === "FINALIZADO" && textoFixo === undefined && !texto) return;
    try {
      await axios.post(`${API}/voip/receptivo/solicitacoes/${sol.id}/atendimento`, { status, texto });
      toast.success(status === "FINALIZADO" ? "Atendimento finalizado!" : "Solicitação adiada.");
      carregar();
    } catch (e) { toast.error("Erro ao atualizar atendimento"); }
  };

  const enviarSMS = async (sol) => {
    const tel = telLimpo(sol.telefone);
    if (tel.length < 10) return toast.error("Telefone inválido");
    const texto = window.prompt(`Mensagem SMS para ${tel}:`, "");
    if (!texto) return;
    try {
      await axios.post(`${API}/sms/enviar`, { numeros: [tel], texto });
      toast.success("SMS enviado!");
      registrarOcorrencia(sol, `SMS enviado: "${texto}"`, "SMS", true);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao enviar SMS"); }
  };

  const ligarVoip = (sol) => {
    const tel = telLimpo(sol.telefone);
    if (tel.length < 10) return toast.error("Telefone inválido");
    window.dispatchEvent(new CustomEvent("softphone-dial", { detail: { numero: tel } }));
    toast.success("Discando no softphone...");
    registrarOcorrencia(sol, "Ligação VoIP iniciada pelo atendente", "LIGACAO", true);
  };

  const contatarWpp = (sol) => {
    if (onContatoWhatsapp) onContatoWhatsapp(sol);
    registrarOcorrencia(sol, "Conversa de WhatsApp iniciada pelo atendente", "WHATSAPP", true);
  };

  // Filtra: some os FINALIZADOS da area de atendimento. Adiados vao pro fim.
  const filtrada = lista
    .filter((s) => s.atendimento_status !== "FINALIZADO")
    .filter((s) => !busca ||
      `${s.telefone || ""} ${s.cpf || ""} ${s.cliente_nome || ""} ${s.segmento || ""} ${s.fila_nome || ""}`
        .toLowerCase().includes(busca.toLowerCase()))
    .sort((a, b) => (a.atendimento_status === "ADIADO" ? 1 : 0) - (b.atendimento_status === "ADIADO" ? 1 : 0));

  return (
    <div className="flex flex-col h-full" data-testid="ura-solicitacoes-panel">
      <div className="p-2 border-b bg-pink-50 flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="h-3.5 w-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
          <input
            data-testid="ura-busca-input"
            className="w-full text-xs border rounded-full pl-7 pr-2 py-1.5 focus:outline-none focus:border-pink-400"
            placeholder="Buscar telefone, CPF, nome..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
        <button data-testid="ura-refresh-btn" onClick={carregar} className="p-1.5 rounded-full hover:bg-pink-100 text-pink-600" title="Atualizar">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="text-center py-12 text-gray-400 text-xs">Carregando...</div>
        ) : filtrada.length === 0 ? (
          <div className="text-center py-12 text-gray-400"><ClipboardList className="h-8 w-8 mx-auto mb-2 opacity-30" /><p className="text-xs">Nenhuma solicitação da URA</p></div>
        ) : (
          filtrada.map((s, idx) => {
            const adiado = s.atendimento_status === "ADIADO";
            return (
            <div key={s.id || idx} className={`px-3 py-2 border-b ${adiado ? "bg-amber-50 border-l-4 border-l-amber-400" : "hover:bg-gray-50"}`} data-testid={`ura-item-${idx}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono font-semibold text-xs text-gray-800">{s.telefone || "sem telefone"}</span>
                <span className="text-[9px] text-gray-400">{fmtData(s.data_hora)}</span>
              </div>
              <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                {adiado && <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-amber-200 text-amber-800 flex items-center gap-0.5"><Clock className="h-2 w-2" />ADIADO</span>}
                {s.segmento && <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-pink-100 text-pink-700">{s.segmento}</span>}
                {s.fila_nome && <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">{s.fila_nome}</span>}
                {s.status && <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{s.status}</span>}
                {s.tag_cliente && <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">{s.tag_cliente}</span>}
              </div>
              {(s.cliente_nome || s.cpf) && (
                <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-600">
                  {s.cliente_nome && <span className="flex items-center gap-1 truncate"><User className="h-2.5 w-2.5" />{s.cliente_nome}</span>}
                  {s.cpf && <span className="flex items-center gap-1 font-mono"><CreditCard className="h-2.5 w-2.5" />{s.cpf}</span>}
                </div>
              )}
              {Array.isArray(s.ocorrencias) && s.ocorrencias.length > 0 && (
                <div className="mt-1 pl-2 border-l-2 border-pink-200 space-y-0.5">
                  {s.ocorrencias.slice(-3).map((o, i) => (
                    <p key={i} className="text-[9px] text-gray-500 truncate"><span className="font-bold text-pink-600">[{o.tipo || "NOTA"}]</span> {o.texto}</p>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-1 mt-1.5">
                <button data-testid={`ura-wpp-${idx}`} onClick={() => contatarWpp(s)} title="Contatar WhatsApp" className="flex-1 flex items-center justify-center gap-1 text-[10px] font-semibold py-1 rounded bg-green-600 hover:bg-green-700 text-white">
                  <MessageCircle className="h-3 w-3" /> WhatsApp
                </button>
                <button data-testid={`ura-sms-${idx}`} onClick={() => enviarSMS(s)} title="Enviar SMS" className="flex-1 flex items-center justify-center gap-1 text-[10px] font-semibold py-1 rounded bg-amber-500 hover:bg-amber-600 text-white">
                  <Send className="h-3 w-3" /> SMS
                </button>
                <button data-testid={`ura-voip-${idx}`} onClick={() => ligarVoip(s)} title="Ligar via VoIP" className="flex-1 flex items-center justify-center gap-1 text-[10px] font-semibold py-1 rounded bg-blue-600 hover:bg-blue-700 text-white">
                  <Phone className="h-3 w-3" /> Ligar
                </button>
                <button data-testid={`ura-ocorrencia-${idx}`} onClick={() => registrarOcorrencia(s)} title="Registrar ocorrência" className="flex items-center justify-center py-1 px-2 rounded bg-gray-600 hover:bg-gray-700 text-white">
                  <ClipboardList className="h-3 w-3" />
                </button>
              </div>
              <div className="flex items-center gap-1 mt-1">
                <button data-testid={`ura-finalizar-${idx}`} onClick={() => setStatus(s, "FINALIZADO")} title="Finalizar atendimento" className="flex-1 flex items-center justify-center gap-1 text-[10px] font-semibold py-1 rounded bg-red-600 hover:bg-red-700 text-white">
                  <CheckCircle2 className="h-3 w-3" /> Finalizar
                </button>
                <button data-testid={`ura-adiar-${idx}`} onClick={() => setStatus(s, "ADIADO", "Solicitação adiada pelo atendente")} title="Adiar (vai para o fim da fila)" className={`flex-1 flex items-center justify-center gap-1 text-[10px] font-semibold py-1 rounded ${adiado ? "bg-amber-300 text-amber-900" : "bg-amber-400 hover:bg-amber-500 text-amber-900"}`}>
                  <Clock className="h-3 w-3" /> {adiado ? "Adiado" : "Adiar"}
                </button>
              </div>
            </div>
          );})
        )}
      </div>
    </div>
  );
};

export default UraSolicitacoes;
